import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { LogIn, LogOut, X, CalendarClock } from 'lucide-react';
import Button from './ui/Button';
import { useTimesheetClockWidget } from '../hooks/useTimesheetClockWidget';
import type { LeaveOnDay } from '../services/leave';

const LEAVE_TYPES = ['personal', 'annual', 'unpaid', 'other', 'time_in_lieu', 'long_service'];

const leaveLabel = (leave: LeaveOnDay, t: TFunction<'timesheet', 'TimesheetClockButton'>) =>
  LEAVE_TYPES.includes(leave.leave_type) ? t(`leaveType.${leave.leave_type}`) : t('leave');

/**
 * Renders a Clock In or Clock Out button in the page header based on the
 * current user's timesheet licence, work schedule, and clock status.
 *
 * - Clock In: shown when the user is not clocked in and is within ±1 hour of
 *   their scheduled start time.  Clocking in before the scheduled start
 *   (within the configured grace window) snaps the recorded time forward to
 *   the scheduled start; clocking in after the scheduled start records the
 *   actual time.
 * - Clock Out: shown when the user is clocked in and the current time is
 *   within 1 hour before (or any time after) the scheduled end time.
 *   Clocking out after the scheduled end (within the configured grace window)
 *   snaps the recorded time back to the scheduled end; clocking out before
 *   the scheduled end records the actual time (no rounding up of hours).
 *
 * A refused clock-in/out also renders, as a dismissible banner. It is rendered
 * independently of the buttons and survives them: a refusal usually changes the
 * status the buttons are derived from, so tying the message to the button that
 * raised it would delete the explanation at the moment it became relevant.
 *
 * It also carries the answer to a clash the employee is the only one who can
 * settle: leave approved for today, on a day they have turned up to work. That
 * is asked as a question with two ways out, never resolved silently — approved
 * leave and a leave balance are not things to spend on somebody's behalf.
 *
 * Renders nothing when there is no button, no error and no clash.
 */
const TimesheetClockButton: React.FC = () => {
  const { t } = useTranslation('timesheet', { keyPrefix: 'TimesheetClockButton' });
  const {
    showClockIn, showClockOut, loading, retrying,
    error, dismissError,
    leaveConflict, confirmReleaseLeave, dismissLeaveConflict,
    leaveNotice, dismissLeaveNotice,
    handleClockIn, handleClockOut,
  } = useTimesheetClockWidget();

  let button: React.ReactNode = null;

  if (showClockIn) {
    button = (
      <Button
        onClick={handleClockIn}
        isLoading={loading}
        disabled={loading}
        variant="success"
        size="sm"
        aria-label={t('clockIn')}
      >
        <LogIn className="h-4 w-4" />
        {/* The label is normally hidden on a phone to keep the header row
            narrow. While retrying it is the whole point, so it shows at every
            width — a bare spinner for 45 s is what makes an operator press
            again or assume they are on the clock. */}
        <span className={retrying ? 'inline' : 'hidden sm:inline'}>
          {retrying ? t('stillTrying') : t('clockIn')}
        </span>
      </Button>
    );
  } else if (showClockOut) {
    button = (
      <Button
        onClick={() => handleClockOut()}
        isLoading={loading}
        disabled={loading}
        variant="danger"
        size="sm"
        aria-label={t('clockOut')}
      >
        <LogOut className="h-4 w-4" />
        <span className={retrying ? 'inline' : 'hidden sm:inline'}>
          {retrying ? t('stillTrying') : t('clockOut')}
        </span>
      </Button>
    );
  }

  if (!button && !error && !leaveConflict && !leaveNotice) return null;

  return (
    <>
      {button}
      {leaveConflict && (
        // A decision, so it is modal: leaving it dismissable-by-ignoring would
        // let someone tap past it and go back to a Clock In button that keeps
        // refusing without ever saying why again.
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="leave-conflict-title"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <div className="min-w-0 flex-1">
                <h2 id="leave-conflict-title" className="text-base font-semibold text-gray-900">
                  {t('conflictTitle', { leave: leaveLabel(leaveConflict, t) })}
                </h2>
                <p className="mt-2 break-words text-sm text-gray-600">
                  {leaveConflict.hours > 0
                    ? t('conflictHours', { hours: leaveConflict.hours })
                    : t('conflictNoHours')}
                  {t('conflictRelease')}
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" size="sm" onClick={dismissLeaveConflict} disabled={loading}>
                {t('keepLeave')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={confirmReleaseLeave}
                isLoading={loading}
                disabled={loading}
              >
                {retrying ? t('stillTrying') : t('releaseLeave')}
              </Button>
            </div>
          </div>
        </div>
      )}
      {leaveNotice && (
        <div className="fixed inset-x-0 bottom-0 z-50 p-4 pointer-events-none">
          <div
            role="status"
            className="pointer-events-auto mx-auto flex max-w-md items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 shadow-lg"
          >
            <p className="min-w-0 flex-1 break-words text-sm text-amber-900">
              {t('noticePartDay', {
                leave: leaveLabel(leaveNotice, t),
                start: leaveNotice.start_time,
                end: leaveNotice.end_time,
              })}
            </p>
            <button
              type="button"
              onClick={dismissLeaveNotice}
              aria-label={t('dismiss')}
              className="shrink-0 rounded p-1 text-amber-700 hover:bg-amber-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      {error && (
        // Fixed to the viewport rather than placed beside the button: the header
        // is the tightest row on a 320px phone and has no room for a sentence.
        <div className="fixed inset-x-0 bottom-0 z-50 p-4 pointer-events-none">
          <div
            role="alert"
            className="pointer-events-auto mx-auto flex max-w-md items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-3 shadow-lg"
          >
            <p className="min-w-0 flex-1 break-words text-sm text-red-800">{error}</p>
            <button
              type="button"
              onClick={dismissError}
              aria-label={t('dismiss')}
              className="shrink-0 rounded p-1 text-red-600 hover:bg-red-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TimesheetClockButton;
